import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LayoutGrid, LayoutList, CalendarDays, GanttChart, Plus, ZoomIn, ZoomOut, Search, Users, Flag, GitBranch, Diamond, Sparkles, X as XIcon, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// ---------- constants ----------
const STATUSES = [
  { key: "todo", label: "To Do", color: "#94A3B8" },
  { key: "in_progress", label: "In Progress", color: "#3B82F6" },
  { key: "blocked", label: "Blocked", color: "#EF4444" },
  { key: "review", label: "In Review", color: "#F59E0B" },
  { key: "done", label: "Done", color: "#22C55E" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));
const PRIORITIES = ["low", "medium", "high", "critical"];
const PRIORITY_COLOR = { low: "border-slate-400", medium: "border-sky-500", high: "border-amber-500", critical: "border-rose-500" };

const dayMs = 24 * 60 * 60 * 1000;
const parseDate = (s) => (s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")) : null);
const toISO = (d) => d.toISOString().slice(0, 10);
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const addDays = (d, n) => new Date(d.getTime() + n * dayMs);
const daysBetween = (a, b) => Math.round((b - a) / dayMs);

// ---------- Main component ----------
export default function TimelineTab({ pid, project, user }) {
  const [view, setView] = useState("gantt"); // gantt | kanban | calendar | list
  const [tasks, setTasks] = useState([]);
  const [deps, setDeps] = useState([]);           // Phase B — dependency edges
  const [members, setMembers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);   // Phase B — template picker
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, e, p, dp] = await Promise.all([
        api.get(`/projects/${pid}/works`),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get(`/projects/${pid}/phases`).catch(() => ({ data: [] })),
        api.get(`/projects/${pid}/dependencies-all`).catch(() => ({ data: [] })),
      ]);
      setTasks(w.data || []);
      setMembers(e.data || []);
      setPhases(p.data || []);
      setDeps(dp.data || []);
    } catch (e) { toast.error("Failed to load timeline"); }
    finally { setLoading(false); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const memberOf = useCallback((id) => members.find((m) => m.id === id), [members]);

  const filtered = useMemo(() => {
    let arr = tasks;
    if (filterStatus !== "all") arr = arr.filter((t) => t.status === filterStatus);
    if (filterAssignee !== "all") arr = arr.filter((t) => (t.assigned_to || "") === filterAssignee);
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter((t) => (t.title || "").toLowerCase().includes(s) || (t.description || "").toLowerCase().includes(s));
    }
    return arr;
  }, [tasks, filterStatus, filterAssignee, q]);

  const patch = async (task, changes) => {
    // Optimistic update
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, ...changes } : t)));
    try { await api.patch(`/projects/${pid}/works/${task.id}`, changes); }
    catch (e) { toast.error("Update failed"); load(); }
  };

  const remove = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try { await api.delete(`/projects/${pid}/works/${task.id}`); setTasks((ts) => ts.filter((t) => t.id !== task.id)); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  // Progress summary (used in header + list view)
  const stats = useMemo(() => {
    const now = new Date();
    let done = 0, inprog = 0, overdue = 0, upcoming = 0;
    for (const t of tasks) {
      if (t.status === "done") done++;
      else if (t.status === "in_progress") inprog++;
      if (t.due_date) {
        const d = parseDate(t.due_date);
        if (d && d < now && t.status !== "done") overdue++;
        else if (d && daysBetween(now, d) <= 7 && t.status !== "done") upcoming++;
      }
    }
    return { total: tasks.length, done, inprog, overdue, upcoming, pct: tasks.length ? Math.round((done / tasks.length) * 100) : 0 };
  }, [tasks]);

  return (
    <div className="space-y-4" data-testid="project-timeline">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <div className="ml-3 flex items-center gap-3 text-xs text-muted-foreground">
            <span data-testid="stat-total"><b className="text-foreground">{stats.total}</b> tasks</span>
            <span className="text-emerald-500"><b>{stats.done}</b> done</span>
            <span className="text-sky-500"><b>{stats.inprog}</b> in progress</span>
            {stats.overdue > 0 && <span className="text-rose-500"><b>{stats.overdue}</b> overdue</span>}
            {stats.upcoming > 0 && <span className="text-amber-500"><b>{stats.upcoming}</b> due &lt; 7d</span>}
            <span>· <b className="text-foreground">{stats.pct}%</b></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="h-8 pl-8 w-56" data-testid="tl-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-36" data-testid="tl-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger className="h-8 w-40" data-testid="tl-filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All owners</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setShowTemplates(true)} data-testid="tl-open-templates"><Sparkles className="w-3.5 h-3.5 mr-1" />Templates</Button>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="tl-add-task" className="bg-primary text-primary-foreground"><Plus className="w-3.5 h-3.5 mr-1" />Task</Button>
        </div>
      </div>

      {/* Empty-state helper — quick template shortcut */}
      {!loading && tasks.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-10 text-center" data-testid="timeline-empty">
          <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
          <div className="text-sm font-medium mb-1">Start with a Timeline Template</div>
          <div className="text-xs text-muted-foreground mb-4">Pick a construction or interiors template — tasks, dependencies and milestones will be created for you.</div>
          <Button size="sm" onClick={() => setShowTemplates(true)} className="bg-primary text-primary-foreground" data-testid="tl-empty-templates">Browse templates</Button>
        </div>
      )}

      {/* Body */}
      {loading && <div className="rounded-2xl border border-border/60 bg-card p-16 text-center text-muted-foreground">Loading timeline…</div>}
      {!loading && tasks.length > 0 && view === "gantt" && <GanttView tasks={filtered} deps={deps} onPatch={patch} onEdit={setEditing} project={project} memberOf={memberOf} pid={pid} onReload={load} />}
      {!loading && tasks.length > 0 && view === "kanban" && <KanbanView tasks={filtered} onPatch={patch} onEdit={setEditing} memberOf={memberOf} />}
      {!loading && tasks.length > 0 && view === "calendar" && <CalendarView tasks={filtered} onEdit={setEditing} onPatch={patch} />}
      {!loading && tasks.length > 0 && view === "list" && <ListView tasks={filtered} deps={deps} onEdit={setEditing} onDelete={remove} onPatch={patch} memberOf={memberOf} phases={phases} />}

      {/* Task editor */}
      <TaskDialog
        open={!!editing || creating}
        task={editing}
        pid={pid}
        tasks={tasks}
        deps={deps}
        members={members}
        phases={phases}
        project={project}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setEditing(null); setCreating(false); load(); }}
      />

      {/* Timeline template picker */}
      <TemplatePickerDialog
        open={showTemplates}
        pid={pid}
        projectStart={project?.start_date}
        hasTasks={tasks.length > 0}
        onClose={() => setShowTemplates(false)}
        onApplied={() => { setShowTemplates(false); load(); }}
      />
    </div>
  );
}

// ---------- View switcher ----------
function ViewSwitcher({ view, setView }) {
  const items = [
    { key: "gantt", label: "Gantt", icon: GanttChart },
    { key: "kanban", label: "Kanban", icon: LayoutGrid },
    { key: "calendar", label: "Calendar", icon: CalendarDays },
    { key: "list", label: "List", icon: LayoutList },
  ];
  return (
    <div className="inline-flex rounded-lg border border-border/60 bg-muted/20 p-0.5" data-testid="view-switcher">
      {items.map((it) => {
        const Icon = it.icon;
        const active = view === it.key;
        return (
          <button
            key={it.key}
            onClick={() => setView(it.key)}
            data-testid={`view-${it.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${active ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- KANBAN view ----------
function KanbanView({ tasks, onPatch, onEdit, memberOf }) {
  const [drag, setDrag] = useState(null); // task id being dragged
  const drop = (e, status) => {
    e.preventDefault();
    if (drag) {
      const task = tasks.find((t) => t.id === drag);
      if (task && task.status !== status) onPatch(task, { status });
      setDrag(null);
    }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kanban-view">
      {STATUSES.map((s) => {
        const cards = tasks.filter((t) => t.status === s.key);
        return (
          <div key={s.key} data-testid={`kanban-col-${s.key}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => drop(e, s.key)}
            className="rounded-xl bg-muted/30 border border-border/40 p-2 min-h-[400px]">
            <div className="flex items-center justify-between px-1 py-1.5">
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: s.color }} /><span className="text-[11px] uppercase tracking-widest font-semibold">{s.label}</span></div>
              <span className="text-[10px] text-muted-foreground">{cards.length}</span>
            </div>
            <div className="space-y-2 mt-1">
              {cards.map((t) => (
                <div key={t.id} data-testid={`kanban-card-${t.id}`} draggable
                  onDragStart={() => setDrag(t.id)} onDragEnd={() => setDrag(null)}
                  onClick={() => onEdit(t)}
                  className={`rounded-lg bg-card border-l-2 ${PRIORITY_COLOR[t.priority] || "border-border"} border-r border-t border-b border-border/60 p-2.5 cursor-pointer hover:shadow-md transition-shadow`}>
                  <div className="text-xs font-medium leading-snug">{t.title}</div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{t.category || "General"}</span>
                    {t.due_date && <span>{new Date(t.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>}
                  </div>
                  {t.progress > 0 && (
                    <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
                      <div className="h-full" style={{ width: `${t.progress}%`, background: s.color }} />
                    </div>
                  )}
                  {t.assigned_to && (
                    <div className="mt-1.5 text-[10px] text-muted-foreground truncate">{memberOf(t.assigned_to)?.name || "—"}</div>
                  )}
                </div>
              ))}
              {cards.length === 0 && <div className="text-center py-4 text-[11px] text-muted-foreground">Drop tasks here</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- LIST view ----------
// ---------- Inline progress bar (click-to-set + drag) ----------
function InlineProgressBar({ task, onPatch }) {
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const progress = preview != null ? preview : (task.progress || 0);
  const setPct = useCallback((clientX) => {
    const el = barRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const pct = Math.round(clamp(((clientX - rect.left) / rect.width) * 100, 0, 100));
    return pct;
  }, []);
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => { const p = setPct(e.clientX); if (p != null) setPreview(p); };
    const up = (e) => {
      const p = setPct(e.clientX);
      setDragging(false);
      setPreview(null);
      if (p != null && p !== (task.progress || 0)) {
        // If moved to 100, also mark done. If moved off 100, revert to in_progress.
        const changes = { progress: p };
        if (p === 100 && task.status !== "done") changes.status = "done";
        else if (p > 0 && p < 100 && task.status === "todo") changes.status = "in_progress";
        onPatch(task, changes);
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [dragging, task, onPatch, setPct]);
  return (
    <div className="flex items-center gap-2" data-no-row-open>
      <div
        ref={barRef}
        data-no-row-open
        data-testid={`row-progress-${task.id}`}
        onMouseDown={(e) => {
          e.stopPropagation();
          const p = setPct(e.clientX);
          if (p != null) setPreview(p);
          setDragging(true);
        }}
        className="h-2.5 flex-1 rounded-full bg-slate-200/70 dark:bg-slate-700/70 overflow-hidden cursor-ew-resize border border-border/50 hover:border-primary/60 min-w-[80px]"
        title="Drag to set progress"
      >
        <div
          className="h-full transition-[width] duration-100"
          style={{ width: `${progress}%`, background: STATUS_MAP[task.status]?.color || "#22C55E" }}
        />
      </div>
      <span className={`text-[10px] w-9 text-right tabular-nums font-medium ${progress === 100 ? "text-emerald-500" : "text-muted-foreground"}`}>{progress}%</span>
    </div>
  );
}

// ---------- LIST view ----------
function ListView({ tasks, deps = [], onEdit, onDelete, onPatch, memberOf, phases }) {
  const [sortBy, setSortBy] = useState("wbs");
  const depth = useCallback((t, all) => {
    let d = 0, cur = t, guard = 0;
    while (cur && cur.parent_id && guard < 20) {
      cur = all.find((x) => x.id === cur.parent_id); if (!cur) break;
      d++; guard++;
    }
    return d;
  }, []);
  const wbsKey = (t) => (t.wbs_code || "999").split(".").map((n) => String(parseInt(n, 10) || 999).padStart(4, "0")).join(".");
  const sorted = useMemo(() => {
    const arr = [...tasks];
    if (sortBy === "wbs") arr.sort((a, b) => wbsKey(a).localeCompare(wbsKey(b)));
    else if (sortBy === "due") arr.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    else if (sortBy === "priority") { const p = { critical: 0, high: 1, medium: 2, low: 3 }; arr.sort((a, b) => (p[a.priority] ?? 5) - (p[b.priority] ?? 5)); }
    else if (sortBy === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    else if (sortBy === "progress") arr.sort((a, b) => (b.progress || 0) - (a.progress || 0));
    return arr;
  }, [tasks, sortBy]);
  const depsByTo = useMemo(() => {
    const m = {}; for (const d of (deps || [])) { (m[d.to_id] = m[d.to_id] || []).push(d); } return m;
  }, [deps]);
  return (
    <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="list-view">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-muted/20 text-xs">
        <div className="uppercase tracking-widest text-muted-foreground">Tasks · {tasks.length}</div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-7 w-32 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="wbs">By WBS</SelectItem>
            <SelectItem value="due">By due date</SelectItem>
            <SelectItem value="priority">By priority</SelectItem>
            <SelectItem value="status">By status</SelectItem>
            <SelectItem value="progress">By progress</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground sticky top-0">
            <tr>
              <th className="text-left w-20 pl-4">WBS</th>
              <th className="text-left py-2.5 px-2">Task</th>
              <th className="text-left w-28">Category</th>
              <th className="text-left w-28">Owner</th>
              <th className="text-left w-24">Priority</th>
              <th className="text-left w-32">Status</th>
              <th className="text-left w-28">Due</th>
              <th className="text-left w-40">Progress</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">No tasks match.</td></tr>}
            {sorted.map((t) => {
              const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== "done";
              const dp = depth(t, tasks);
              const nDeps = (depsByTo[t.id] || []).length;
              return (
                <tr
                  key={t.id}
                  data-testid={`list-row-${t.id}`}
                  onClick={(e) => {
                    // Ignore clicks that originated in the status Select, the progress slider or the delete button
                    const target = e.target;
                    if (target.closest("[data-no-row-open], button, select, input, [role=combobox], [role=option]")) return;
                    onEdit(t);
                  }}
                  className={`border-t border-border/50 hover:bg-muted/20 cursor-pointer ${t.is_milestone ? "bg-amber-500/5" : ""}`}
                >
                  <td className="text-[10px] font-mono text-muted-foreground pl-4">{t.wbs_code || "—"}</td>
                  <td className="py-2.5 px-2">
                    <div className="text-left flex items-center gap-2" style={{ paddingLeft: dp * 16 }}>
                      {t.is_milestone
                        ? <Diamond className="w-3 h-3 shrink-0 text-amber-500 fill-amber-500" />
                        : dp > 0
                          ? <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />
                          : <span className="w-3" />}
                      <div>
                        <div className={`font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""} ${t.is_milestone ? "text-amber-600" : ""}`}>{t.title}</div>
                        {t.description && <div className="text-[11px] text-muted-foreground truncate max-w-md">{t.description}</div>}
                        {nDeps > 0 && <div className="text-[9px] text-slate-500 flex items-center gap-1 mt-0.5"><GitBranch className="w-2.5 h-2.5" />{nDeps} dep{nDeps > 1 ? "s" : ""}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="text-xs text-muted-foreground">{t.category || "—"}</td>
                  <td className="text-xs">{memberOf(t.assigned_to)?.name || "—"}</td>
                  <td>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border-l-2 ${PRIORITY_COLOR[t.priority] || "border-border"} bg-muted/40`}>{t.priority || "—"}</span>
                  </td>
                  <td data-no-row-open>
                    <Select value={t.status} onValueChange={(v) => onPatch(t, { status: v })}>
                      <SelectTrigger className="h-7 text-[11px] w-28" data-no-row-open><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className={`text-xs ${overdue ? "text-rose-500 font-medium" : ""}`}>{t.due_date ? new Date(t.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</td>
                  <td data-no-row-open>
                    {t.is_milestone
                      ? <span className="text-[10px] uppercase tracking-widest text-amber-500">Milestone</span>
                      : <InlineProgressBar task={t} onPatch={onPatch} />}
                  </td>
                  <td data-no-row-open>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(t); }} data-testid={`list-del-${t.id}`} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive">×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- CALENDAR view ----------
function CalendarView({ tasks, onEdit }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(month.getFullYear(), month.getMonth(), d));
  const dayTasks = (d) => {
    const iso = toISO(d);
    return tasks.filter((t) => (t.due_date && t.due_date.slice(0, 10) === iso) || (t.start_date && t.start_date.slice(0, 10) === iso));
  };
  return (
    <div className="rounded-2xl bg-card border border-border/60 p-4" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-border/40 rounded-lg overflow-hidden">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-muted/40 py-1.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">{d}</div>)}
        {days.map((d, i) => {
          if (!d) return <div key={i} className="bg-background/40 min-h-[90px]" />;
          const isToday = toISO(d) === toISO(new Date());
          const ts = dayTasks(d);
          return (
            <div key={i} data-testid={`cal-day-${toISO(d)}`} className={`bg-background min-h-[90px] p-1.5 ${isToday ? "ring-1 ring-primary/50" : ""}`}>
              <div className={`text-[10px] font-medium mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d.getDate()}</div>
              <div className="space-y-1">
                {ts.slice(0, 3).map((t) => (
                  <button key={t.id} onClick={() => onEdit(t)} data-testid={`cal-task-${t.id}`}
                    className="w-full text-left rounded px-1.5 py-0.5 text-[10px] truncate hover:opacity-80"
                    style={{ background: (STATUS_MAP[t.status]?.color || "#666") + "22", color: STATUS_MAP[t.status]?.color || undefined }}>
                    {t.title}
                  </button>
                ))}
                {ts.length > 3 && <div className="text-[9px] text-muted-foreground">+{ts.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- GANTT view ----------
function GanttView({ tasks, deps = [], onPatch, onEdit, project, memberOf, pid, onReload }) {
  const [zoom, setZoom] = useState(2); // 0=day, 1=week, 2=month, 3=quarter
  const zoomLabel = ["Day", "Week", "Month", "Quarter"][zoom];
  const dayWidth = [40, 18, 10, 4][zoom];

  // Determine visible date range (bounded by task dates or project dates)
  const range = useMemo(() => {
    const dates = [];
    for (const t of tasks) {
      const s = parseDate(t.start_date); const e = parseDate(t.due_date);
      if (s) dates.push(s); if (e) dates.push(e);
    }
    if (project?.start_date) dates.push(parseDate(project.start_date));
    if (project?.end_date) dates.push(parseDate(project.end_date));
    let min, max;
    if (dates.length) { min = new Date(Math.min(...dates.map((d) => d.getTime()))); max = new Date(Math.max(...dates.map((d) => d.getTime()))); }
    else { min = new Date(); max = addDays(min, 60); }
    min = addDays(min, -3); max = addDays(max, 14);
    return { min, max, days: daysBetween(min, max) + 1 };
  }, [tasks, project]);

  const scrollRef = useRef(null);
  const [drag, setDrag] = useState(null); // {taskId, mode: move|resize-start|resize-end, originX, origStart, origDue}

  useEffect(() => {
    const onMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.originX;
      const days = Math.round(dx / dayWidth);
      if (days === 0) return;
      const task = tasks.find((t) => t.id === drag.taskId);
      if (!task) return;
      const s = parseDate(drag.origStart) || parseDate(drag.origDue);
      const d = parseDate(drag.origDue) || parseDate(drag.origStart);
      if (drag.mode === "move") {
        const ns = s ? addDays(s, days) : null;
        const nd = d ? addDays(d, days) : null;
        if (ns) task._preview_start = toISO(ns);
        if (nd) task._preview_due = toISO(nd);
      } else if (drag.mode === "resize-start") {
        const ns = s ? addDays(s, days) : null;
        if (ns && d && ns < d) task._preview_start = toISO(ns);
      } else if (drag.mode === "resize-end") {
        const nd = d ? addDays(d, days) : null;
        if (nd && s && nd > s) task._preview_due = toISO(nd);
      }
      setTasksTick((n) => n + 1);
    };
    const onUp = () => {
      if (!drag) return;
      const task = tasks.find((t) => t.id === drag.taskId);
      if (task && (task._preview_start || task._preview_due)) {
        const changes = {};
        if (task._preview_start && task._preview_start !== task.start_date) changes.start_date = task._preview_start;
        if (task._preview_due && task._preview_due !== task.due_date) changes.due_date = task._preview_due;
        delete task._preview_start; delete task._preview_due;
        if (Object.keys(changes).length) onPatch(task, changes);
      }
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag, tasks, dayWidth, onPatch]);
  const [, setTasksTick] = useState(0);

  const totalWidth = range.days * dayWidth;

  // Month/week header ticks
  const headerTicks = useMemo(() => {
    const ticks = [];
    for (let i = 0; i < range.days; i++) {
      const d = addDays(range.min, i);
      if (zoom === 0) ticks.push({ x: i * dayWidth, label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }), major: d.getDay() === 1 });
      else if (zoom === 1 && d.getDay() === 1) ticks.push({ x: i * dayWidth, label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }), major: d.getDate() < 8 });
      else if (zoom === 2 && d.getDate() === 1) ticks.push({ x: i * dayWidth, label: d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }), major: true });
      else if (zoom === 3 && d.getDate() === 1 && [0, 3, 6, 9].includes(d.getMonth())) ticks.push({ x: i * dayWidth, label: `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`, major: true });
    }
    return ticks;
  }, [range, zoom, dayWidth]);

  const todayX = daysBetween(range.min, new Date()) * dayWidth;

  return (
    <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="gantt-view">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <div className="text-xs text-muted-foreground">Drag any bar to reschedule · edges to resize</div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setZoom(clamp(zoom - 1, 0, 3))} disabled={zoom === 0} data-testid="gantt-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></Button>
          <span className="text-xs text-muted-foreground px-2">{zoomLabel}</span>
          <Button variant="outline" size="sm" onClick={() => setZoom(clamp(zoom + 1, 0, 3))} disabled={zoom === 3} data-testid="gantt-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="flex" data-testid="gantt-body">
        {/* Left task list */}
        <div className="w-72 shrink-0 border-r border-border/60 bg-muted/20">
          <div className="h-10 border-b border-border/50 flex items-center px-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="w-10">WBS</span>
            <span>Task</span>
          </div>
          {tasks.length === 0 && <div className="text-center py-10 text-xs text-muted-foreground">Add tasks to see the timeline.</div>}
          {tasks.map((t) => (
            <div key={t.id} className="h-9 border-b border-border/40 flex items-center px-3 gap-2 text-xs cursor-pointer hover:bg-muted/30"
              onClick={() => onEdit(t)} data-testid={`gantt-label-${t.id}`}>
              <span className="w-10 shrink-0 text-[9px] text-muted-foreground font-mono">{t.wbs_code || "—"}</span>
              {t.is_milestone
                ? <Diamond className="w-2.5 h-2.5 shrink-0 text-amber-500 fill-amber-500" />
                : <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_MAP[t.status]?.color || "#666" }} />}
              <span className={`truncate flex-1 ${t.parent_id ? "pl-2" : "font-medium"}`}>{t.title}</span>
              <span className="text-[9px] text-muted-foreground">{memberOf(t.assigned_to)?.name?.split(" ")[0] || ""}</span>
            </div>
          ))}
        </div>
        {/* Right timeline */}
        <div ref={scrollRef} className="flex-1 overflow-x-auto" style={{ userSelect: drag ? "none" : "auto" }}>
          <div style={{ width: totalWidth, minWidth: "100%", position: "relative" }}>
            {/* Header ticks */}
            <div className="h-10 border-b border-border/50 relative">
              {headerTicks.map((t, i) => (
                <div key={i} className={`absolute top-0 h-full ${t.major ? "border-l border-border/70" : "border-l border-border/30"} pl-1 text-[10px] ${t.major ? "text-foreground" : "text-muted-foreground"}`} style={{ left: t.x }}>{t.label}</div>
              ))}
              {todayX >= 0 && todayX <= totalWidth && (
                <div className="absolute top-0 bottom-0 w-px bg-primary z-10" style={{ left: todayX }} title="Today" />
              )}
            </div>
            {/* Rows */}
            {tasks.map((t) => {
              const s = parseDate(t._preview_start || t.start_date) || parseDate(t._preview_due || t.due_date);
              const d = parseDate(t._preview_due || t.due_date) || parseDate(t._preview_start || t.start_date);
              if (!s || !d) {
                return <div key={t.id} className="h-9 border-b border-border/40 relative">
                  <div className="absolute top-1/2 -translate-y-1/2 left-2 text-[10px] text-muted-foreground italic">No dates — click to set</div>
                </div>;
              }
              const x1 = daysBetween(range.min, s) * dayWidth;
              const x2 = daysBetween(range.min, d) * dayWidth + dayWidth;
              const w = Math.max(dayWidth, x2 - x1);
              const color = STATUS_MAP[t.status]?.color || "#666";
              const progress = t.progress || 0;
              // ---- Milestone render as a diamond ----
              if (t.is_milestone) {
                const cx = x1 + dayWidth / 2;
                return (
                  <div key={t.id} className="h-9 border-b border-border/40 relative">
                    {todayX >= 0 && todayX <= totalWidth && <div className="absolute top-0 bottom-0 w-px bg-primary/40 z-0" style={{ left: todayX }} />}
                    <div
                      data-testid={`gantt-milestone-${t.id}`}
                      onDoubleClick={() => onEdit(t)}
                      onClick={() => onEdit(t)}
                      style={{ left: cx - 8, top: 6 }}
                      className="absolute w-4 h-4 rotate-45 bg-amber-500 border border-amber-600 cursor-pointer hover:scale-125 transition-transform shadow"
                      title={`${t.wbs_code ? "["+t.wbs_code+"] " : ""}${t.title} · ${t.due_date || t.start_date}`}
                    />
                    <div className="absolute top-1.5 h-6 flex items-center pl-1 text-[10px] text-amber-600 font-medium truncate pointer-events-none" style={{ left: cx + 10, maxWidth: 220 }}>{t.title}</div>
                  </div>
                );
              }
              return (
                <div key={t.id} className="h-9 border-b border-border/40 relative">
                  {todayX >= 0 && todayX <= totalWidth && <div className="absolute top-0 bottom-0 w-px bg-primary/40 z-0" style={{ left: todayX }} />}
                  <div
                    data-testid={`gantt-bar-${t.id}`}
                    onMouseDown={(e) => setDrag({ taskId: t.id, mode: "move", originX: e.clientX, origStart: t.start_date, origDue: t.due_date })}
                    onDoubleClick={() => onEdit(t)}
                    style={{ left: x1, width: w, background: color + "1f", borderColor: color }}
                    className="absolute top-1.5 h-6 rounded-md border cursor-grab active:cursor-grabbing flex items-center overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${color}55 ${progress}%, transparent ${progress}%)` }} />
                    <div className="relative px-2 text-[10px] font-medium truncate w-full" style={{ color }}>{t.title}</div>
                    <div onMouseDown={(e) => { e.stopPropagation(); setDrag({ taskId: t.id, mode: "resize-start", originX: e.clientX, origStart: t.start_date, origDue: t.due_date }); }} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/20" />
                    <div onMouseDown={(e) => { e.stopPropagation(); setDrag({ taskId: t.id, mode: "resize-end", originX: e.clientX, origStart: t.start_date, origDue: t.due_date }); }} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/20" />
                  </div>
                </div>
              );
            })}

            {/* Dependency arrows overlay */}
            <DependencyOverlay tasks={tasks} deps={deps} range={range} dayWidth={dayWidth} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Dependency arrows overlay (SVG) ----------
function DependencyOverlay({ tasks, deps, range, dayWidth }) {
  const layout = useMemo(() => {
    // Row-index lookup (skips no-date tasks in the same order)
    const idx = new Map();
    tasks.forEach((t, i) => idx.set(t.id, i));
    const rowH = 36;                                       // matches h-9 border-b
    const headerH = 40;                                    // matches h-10 header
    const totalW = (daysBetween(range.min, range.max) + 1) * dayWidth;
    const totalH = headerH + tasks.length * rowH;
    const arrows = [];
    for (const d of (deps || [])) {
      const from = tasks.find((t) => t.id === d.from_id);
      const to   = tasks.find((t) => t.id === d.to_id);
      if (!from || !to) continue;
      const fs = parseDate(from.start_date), fe = parseDate(from.due_date);
      const ts = parseDate(to.start_date),   te = parseDate(to.due_date);
      if (!fs || !fe || !ts || !te) continue;
      const kind = d.kind || "finish_to_start";
      const fromX = kind.startsWith("start_") ? daysBetween(range.min, fs) * dayWidth
                                             : daysBetween(range.min, fe) * dayWidth + dayWidth;
      const toX   = kind.endsWith("_start")   ? daysBetween(range.min, ts) * dayWidth
                                             : daysBetween(range.min, te) * dayWidth + dayWidth;
      const fromRow = idx.get(from.id); const toRow = idx.get(to.id);
      if (fromRow == null || toRow == null) continue;
      const fromY = headerH + fromRow * rowH + rowH / 2;
      const toY   = headerH + toRow   * rowH + rowH / 2;
      // Simple L-shaped path with a small offset
      const midX = fromX + Math.max(8, Math.min(24, (toX - fromX) / 2));
      const path = `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX} ${toY}`;
      arrows.push({ id: d.id, path, toX, toY, kind });
    }
    return { totalW, totalH, arrows };
  }, [tasks, deps, range, dayWidth]);
  if (!layout.arrows.length) return null;
  return (
    <svg
      data-testid="gantt-dep-overlay"
      className="absolute top-0 left-0 pointer-events-none"
      width={layout.totalW}
      height={layout.totalH}
      style={{ zIndex: 5 }}
    >
      <defs>
        <marker id="arrow-head" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748B" />
        </marker>
      </defs>
      {layout.arrows.map((a) => (
        <path
          key={a.id}
          d={a.path}
          fill="none"
          stroke="#64748B"
          strokeWidth="1.5"
          strokeOpacity="0.75"
          strokeDasharray={a.kind !== "finish_to_start" ? "4 3" : "0"}
          markerEnd="url(#arrow-head)"
        />
      ))}
    </svg>
  );
}

// ---------- Task editor dialog ----------
function TaskDialog({ open, task, pid, tasks = [], deps = [], members, phases, project, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [linking, setLinking] = useState(false);
  const [linkPred, setLinkPred] = useState("");
  const [linkKind, setLinkKind] = useState("finish_to_start");
  const [linkLag, setLinkLag] = useState(0);
  useEffect(() => {
    if (!open) return;
    setForm(task ? {
      title: task.title || "", description: task.description || "", category: task.category || "General",
      assigned_to: task.assigned_to || "", contractor_id: task.contractor_id || "",
      priority: task.priority || "medium", status: task.status || "todo",
      start_date: task.start_date || "", due_date: task.due_date || "",
      progress: task.progress || 0, depends_on: task.depends_on || "", package: task.package || "",
      parent_id: task.parent_id || "", is_milestone: !!task.is_milestone, wbs_code: task.wbs_code || "",
    } : {
      title: "", description: "", category: "General", assigned_to: "", contractor_id: "",
      priority: "medium", status: "todo",
      start_date: project?.start_date ? project.start_date.slice(0, 10) : toISO(new Date()),
      due_date: "", progress: 0, depends_on: "", package: "",
      parent_id: "", is_milestone: false, wbs_code: "",
    });
    setLinking(false); setLinkPred(""); setLinkKind("finish_to_start"); setLinkLag(0);
  }, [open, task, project]);

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    try {
      // Split into two calls: base fields + wbs/milestone fields (they need the wbs endpoint for cycle-safe reparenting)
      const { parent_id, is_milestone, wbs_code, ...rest } = form;
      if (task) {
        await api.patch(`/projects/${pid}/works/${task.id}`, rest);
        // Only PATCH WBS fields if they actually changed
        const wbsPayload = {};
        if ((parent_id || "") !== (task.parent_id || "")) wbsPayload.parent_id = parent_id || null;
        if (!!is_milestone !== !!task.is_milestone) wbsPayload.is_milestone = !!is_milestone;
        if (Object.keys(wbsPayload).length) {
          await api.patch(`/projects/${pid}/works/${task.id}/wbs`, wbsPayload);
        }
        toast.success("Task updated");
      } else {
        const payload = { ...rest };
        const { data: created } = await api.post(`/projects/${pid}/works`, payload);
        if (parent_id || is_milestone) {
          await api.patch(`/projects/${pid}/works/${created.id}/wbs`, {
            parent_id: parent_id || null, is_milestone: !!is_milestone,
          });
        }
        toast.success("Task created");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const taskDeps = task ? (deps || []).filter((d) => d.to_id === task.id) : [];
  const kindLabel = (k) => ({
    finish_to_start: "Finish → Start (FS)", start_to_start: "Start → Start (SS)",
    finish_to_finish: "Finish → Finish (FF)", start_to_finish: "Start → Finish (SF)",
  }[k] || k);
  const addLink = async () => {
    if (!task || !linkPred) return;
    try {
      await api.post(`/projects/${pid}/works/${task.id}/link`, {
        predecessor_id: linkPred, kind: linkKind, lag_days: Number(linkLag) || 0,
      });
      toast.success("Dependency added");
      setLinking(false); setLinkPred(""); setLinkKind("finish_to_start"); setLinkLag(0);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  const removeLink = async (dep) => {
    try {
      await api.delete(`/projects/${pid}/works/${task.id}/link/${dep.id}`);
      toast.success("Dependency removed");
      onSaved && onSaved();
    } catch (e) { toast.error("Unlink failed"); }
  };

  const parentOptions = useMemo(() => tasks.filter((t) => (!task || t.id !== task.id) && !t.is_milestone), [tasks, task]);
  const predOptions = useMemo(() => (task ? tasks.filter((t) => t.id !== task.id) : []), [tasks, task]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {form.is_milestone && <Diamond className="w-4 h-4 text-amber-500 fill-amber-500" />}
            {task ? "Edit task" : "New task"}
            {form.wbs_code && <span className="text-xs font-mono text-muted-foreground ml-2">[{form.wbs_code}]</span>}
          </DialogTitle>
          <DialogDescription>Set dates, ownership and status. Progress cascades to project phase totals.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" /></div>
          <div><Label>Category / Phase</Label><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Civil, MEP, Finishes…" /></div>
          <div><Label>Package</Label><Input value={form.package || ""} onChange={(e) => setForm({ ...form, package: e.target.value })} /></div>
          <div><Label>Parent task (WBS)</Label>
            <Select value={form.parent_id || "__none__"} onValueChange={(v) => setForm({ ...form, parent_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="task-parent"><SelectValue placeholder="— Root task —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Root task —</SelectItem>
                {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.wbs_code ? `[${p.wbs_code}] ` : ""}{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 h-10 cursor-pointer" data-testid="task-milestone-toggle">
              <input type="checkbox" checked={!!form.is_milestone} onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })} className="w-4 h-4 accent-amber-500" />
              <span className="text-sm flex items-center gap-1"><Diamond className={`w-3.5 h-3.5 ${form.is_milestone ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}`} /> Milestone</span>
            </label>
          </div>
          <div><Label>Owner</Label>
            <Select value={form.assigned_to || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="task-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">— Unassigned —</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Start</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="task-start" /></div>
          <div><Label>{form.is_milestone ? "Milestone date" : "Due"}</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" /></div>
          {!form.is_milestone && (
            <div className="col-span-2">
              <Label>Progress · {form.progress}%</Label>
              <input type="range" min="0" max="100" value={form.progress || 0} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} className="w-full accent-primary" data-testid="task-progress" />
            </div>
          )}
        </div>

        {/* Dependencies — only when editing an existing task */}
        {task && (
          <div className="mt-4 border-t border-border/40 pt-4" data-testid="task-deps-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium flex items-center gap-2"><GitBranch className="w-4 h-4 text-slate-500" /> Predecessors</div>
              {!linking && <Button size="sm" variant="outline" onClick={() => setLinking(true)} data-testid="task-add-dep"><Plus className="w-3 h-3 mr-1" />Add link</Button>}
            </div>
            {taskDeps.length === 0 && !linking && <div className="text-xs text-muted-foreground italic">No predecessors. This task can start any time.</div>}
            <div className="space-y-1.5">
              {taskDeps.map((d) => {
                const pred = tasks.find((t) => t.id === d.from_id);
                return (
                  <div key={d.id} className="flex items-center justify-between rounded border border-border/50 bg-muted/20 px-2.5 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground">{pred?.wbs_code || "—"}</span>
                      <span className="truncate max-w-[220px]">{pred?.title || "(missing)"}</span>
                      <span className="text-[10px] text-slate-500 uppercase">{kindLabel(d.kind).split(" ")[3]?.replace(/[()]/g, "") || d.kind}</span>
                      {d.lag_days ? <span className="text-[10px] text-slate-400">+{d.lag_days}d</span> : null}
                    </div>
                    <button onClick={() => removeLink(d)} className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive" data-testid={`task-unlink-${d.id}`}><XIcon className="w-3 h-3" /></button>
                  </div>
                );
              })}
            </div>
            {linking && (
              <div className="mt-2 rounded border border-primary/40 bg-primary/5 p-2.5 space-y-2" data-testid="task-dep-form">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[11px]">Predecessor task</Label>
                    <Select value={linkPred || "__none__"} onValueChange={(v) => setLinkPred(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="dep-pred"><SelectValue placeholder="Pick a predecessor…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Select —</SelectItem>
                        {predOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.wbs_code ? `[${p.wbs_code}] ` : ""}{p.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px]">Kind</Label>
                    <Select value={linkKind} onValueChange={setLinkKind}>
                      <SelectTrigger data-testid="dep-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="finish_to_start">Finish → Start</SelectItem>
                        <SelectItem value="start_to_start">Start → Start</SelectItem>
                        <SelectItem value="finish_to_finish">Finish → Finish</SelectItem>
                        <SelectItem value="start_to_finish">Start → Finish</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[11px]">Lag (days)</Label><Input type="number" value={linkLag} onChange={(e) => setLinkLag(e.target.value)} data-testid="dep-lag" /></div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => { setLinking(false); setLinkPred(""); }}>Cancel</Button>
                  <Button size="sm" onClick={addLink} disabled={!linkPred} data-testid="dep-save">Add dependency</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <Button onClick={save} data-testid="task-save" className="bg-primary text-primary-foreground mt-2">{task ? "Update task" : "Create task"}</Button>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Timeline Template Picker ----------
function TemplatePickerDialog({ open, pid, projectStart, hasTasks, onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [start, setStart] = useState("");
  const [replace, setReplace] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/timeline-templates")
      .then((r) => setTemplates(r.data || []))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
    setStart(projectStart ? projectStart.slice(0, 10) : toISO(new Date()));
    setSelected(null); setReplace(false);
  }, [open, projectStart]);
  const apply = async () => {
    if (!selected) return toast.error("Pick a template first");
    setApplying(true);
    try {
      const { data } = await api.post(`/projects/${pid}/apply-timeline-template`, {
        template_id: selected.id, start_date: start, replace,
      });
      toast.success(`${data.tasks_created} tasks created from ${data.template}`);
      onApplied && onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
    finally { setApplying(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Timeline Templates</DialogTitle>
          <DialogDescription>Pick a construction-flavoured template. Tasks, hierarchy, dependencies and milestones will be created in one shot.</DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center py-8 text-sm text-muted-foreground">Loading templates…</div>}
        {!loading && (
          <div className="grid gap-3">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelected(t)}
                data-testid={`tpl-card-${t.id}`}
                className={`text-left rounded-lg border p-3 transition-all ${selected?.id === t.id ? "border-primary bg-primary/5 shadow" : "border-border/60 hover:border-border"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {t.name}
                      {t.system && <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary rounded px-1.5 py-0.5">Built-in</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.description || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 flex gap-3">
                      <span>{(t.items || []).length} tasks</span>
                      <span>{(t.items || []).filter((i) => i.is_milestone).length} milestones</span>
                      <span>{(t.items || []).filter((i) => i.depends_on_index != null).length} dependencies</span>
                      <span>Scope · {t.scope}</span>
                    </div>
                  </div>
                  {selected?.id === t.id && <div className="text-primary text-lg">✓</div>}
                </div>
              </button>
            ))}
            {templates.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">No templates yet.</div>}
          </div>
        )}
        <div className="mt-4 border-t border-border/40 pt-4 grid grid-cols-2 gap-3">
          <div>
            <Label>Anchor / start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tpl-start" />
          </div>
          <div className="flex items-end">
            <label className={`flex items-center gap-2 h-10 cursor-pointer ${!hasTasks ? "opacity-50 pointer-events-none" : ""}`}>
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="w-4 h-4 accent-destructive" data-testid="tpl-replace" />
              <span className="text-sm">Replace existing tasks {!hasTasks && "(no tasks yet)"}</span>
            </label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={!selected || applying} className="bg-primary text-primary-foreground" data-testid="tpl-apply">
            {applying ? "Applying…" : `Apply ${selected ? selected.name : "template"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
